import { getMessage } from "../data/locales";
import { getCleanPath } from "./publicPaths";

const EMPTY_FLOW_RETURN = {
  returnTo: "",
  returnLabel: "",
  openLinksInNewTab: false,
};

const LEGAL_DOCUMENT_PATHS = new Set(["/terminos", "/privacidad"]);

/** Per-tab flag: this tab was opened from registro, checkout or a course page. */
export const LEGAL_TAB_OPENED_FROM_FLOW_KEY = "legalTabOpenedFromFlow";

function isLegalDocumentPath(pathname) {
  return LEGAL_DOCUMENT_PATHS.has(getCleanPath(pathname));
}

export function isSafeLocalReturnPath(value) {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.includes("//") &&
    !value.includes("\\")
  );
}

function normalizeSearch(search) {
  if (typeof search !== "string" || search.length === 0) {
    return "";
  }
  return search.startsWith("?") ? search.slice(1) : search;
}

function withQuery(pathname, search) {
  const query = typeof search === "string" ? search : "";
  if (!query) {
    return pathname;
  }
  return `${pathname}${query.startsWith("?") ? query : `?${query}`}`;
}

/**
 * @param {string} value
 * @returns {{ pathname: string, search: string }}
 */
export function splitPathAndSearch(value) {
  if (typeof value !== "string" || value.length === 0) {
    return { pathname: "/", search: "" };
  }
  const queryIndex = value.indexOf("?");
  if (queryIndex === -1) {
    return { pathname: value, search: "" };
  }
  return {
    pathname: value.slice(0, queryIndex),
    search: value.slice(queryIndex),
  };
}

/**
 * @param {string} pathname
 * @param {string} [search]
 * @returns {{ returnTo: string, returnLabel: string, openLinksInNewTab: boolean }}
 */
function identifyFlow(pathname, search = "") {
  const cleanPath = getCleanPath(pathname);
  const returnTo = withQuery(cleanPath, search);

  if (cleanPath === "/NewRegistro" || cleanPath === "/registro") {
    return {
      returnTo,
      returnLabel: getMessage("legal.backToRegister"),
      openLinksInNewTab: true,
    };
  }

  if (cleanPath === "/pago" || cleanPath === "/checkout") {
    return {
      returnTo,
      returnLabel: getMessage("legal.backToCheckout"),
      openLinksInNewTab: true,
    };
  }

  if (cleanPath.startsWith("/curso/")) {
    return {
      returnTo,
      returnLabel: getMessage("legal.backToCourse"),
      openLinksInNewTab: true,
    };
  }

  return EMPTY_FLOW_RETURN;
}

/**
 * Return path and label so legal pages can send the user back to registro/compra
 * instead of home. Links opened from those flows use a new tab, except when the
 * current page is already a legal document.
 *
 * @param {string} pathname
 * @param {string} [search]
 * @returns {{ returnTo: string, returnLabel: string, openLinksInNewTab: boolean }}
 */
export function getLegalFlowReturn(pathname, search = "") {
  const direct = identifyFlow(pathname, search);
  if (direct.openLinksInNewTab) {
    return direct;
  }

  const nested = new URLSearchParams(normalizeSearch(search)).get("returnTo");
  if (!isSafeLocalReturnPath(nested)) {
    return EMPTY_FLOW_RETURN;
  }

  const nestedParts = splitPathAndSearch(nested);
  const nestedFlow = identifyFlow(nestedParts.pathname, nestedParts.search);
  if (!isLegalDocumentPath(pathname)) {
    return nestedFlow;
  }
  // Already on a legal document: keep returnTo, but stay in this tab.
  return {
    ...nestedFlow,
    openLinksInNewTab: false,
  };
}

/**
 * Safe returnTo query used to detect a tab opened from registro, checkout or a course.
 * @param {string} [search]
 * @returns {string}
 */
export function readSafeReturnToQuery(search) {
  const value = new URLSearchParams(normalizeSearch(search)).get("returnTo");
  return isSafeLocalReturnPath(value) ? value : "";
}

/**
 * A brand-new tab opened from a flow: `window.opener` is set, or the URL carries
 * a safe returnTo and the history still has a single entry. `rel="noopener"`
 * usually clears the opener, so the history check is the common case.
 * @param {{ hasOpener: boolean, historyLength: number, hasSafeReturnTo: boolean }} params
 * @returns {boolean}
 */
function isFreshLegalTabFromFlow({ hasOpener, historyLength, hasSafeReturnTo }) {
  if (hasOpener) {
    return true;
  }
  const length = Number(historyLength);
  return Boolean(hasSafeReturnTo) && Number.isFinite(length) && length <= 1;
}

/**
 * @returns {boolean}
 */
function readLegalTabOpenedFromFlow() {
  try {
    return sessionStorage.getItem(LEGAL_TAB_OPENED_FROM_FLOW_KEY) === "1";
  } catch (_) {
    return false;
  }
}

/**
 * Remember a legal tab that was just opened from a flow. Call once on mount.
 * Later jumps between /terminos and /privacidad grow history.length, but
 * sessionStorage stays on this tab. Direct visits are left unmarked.
 * Storage failures keep the previous close rules.
 * @param {{ hasOpener: boolean, historyLength: number, hasSafeReturnTo: boolean }} params
 */
export function markLegalTabOpenedFromFlow({ hasOpener, historyLength, hasSafeReturnTo }) {
  if (!isFreshLegalTabFromFlow({ hasOpener, historyLength, hasSafeReturnTo })) {
    return;
  }
  try {
    sessionStorage.setItem(LEGAL_TAB_OPENED_FROM_FLOW_KEY, "1");
  } catch (_) {
    // Private mode and blocked storage fall back to the history-length check.
  }
}

/**
 * Close the legal tab when it was opened from another page, so the origin form stays intact.
 * Direct visits (no opener, no stored flag, and no single-entry returnTo) navigate instead.
 * @param {{ hasOpener: boolean, historyLength: number, hasSafeReturnTo: boolean }} params
 * @returns {boolean}
 */
export function shouldCloseLegalTab({ hasOpener, historyLength, hasSafeReturnTo }) {
  if (hasOpener) {
    return true;
  }
  if (!hasSafeReturnTo) {
    return false;
  }
  if (readLegalTabOpenedFromFlow()) {
    return true;
  }
  const length = Number(historyLength);
  return Number.isFinite(length) && length <= 1;
}

/**
 * @param {string} documentPath
 * @param {{ returnTo: string }} flowReturn
 * @returns {string}
 */
export function buildLegalDocumentTo(documentPath, flowReturn) {
  if (!flowReturn.returnTo) {
    return documentPath;
  }
  const params = new URLSearchParams({ returnTo: flowReturn.returnTo });
  return `${documentPath}?${params.toString()}`;
}

/**
 * @param {{ returnTo: string, returnLabel: string, openLinksInNewTab: boolean }} flowReturn
 * @returns {{ target?: string, rel?: string, state?: { from: string, returnLabel: string } }}
 */
export function buildLegalDocumentLinkExtras(flowReturn) {
  const extras = {};
  if (flowReturn.openLinksInNewTab) {
    extras.target = "_blank";
    extras.rel = "noopener noreferrer";
  }
  if (flowReturn.returnTo) {
    extras.state = {
      from: flowReturn.returnTo,
      returnLabel: flowReturn.returnLabel,
    };
  }
  return extras;
}

/**
 * @param {{ pathname?: string, search?: string, state?: { from?: string, returnLabel?: string } } | null | undefined} location
 * @returns {{ returnTo: string, returnLabel: string }}
 */
export function resolveLegalReturn(location) {
  const fromState = location?.state?.from;
  const fromQuery = new URLSearchParams(normalizeSearch(location?.search)).get("returnTo");
  const candidate = isSafeLocalReturnPath(fromState) ? fromState : fromQuery;
  const returnTo = isSafeLocalReturnPath(candidate) ? candidate : "/";
  const parts = splitPathAndSearch(returnTo);
  const flow = identifyFlow(parts.pathname, parts.search);
  const stateLabel =
    typeof location?.state?.returnLabel === "string" && location.state.returnLabel.trim()
      ? location.state.returnLabel.trim()
      : "";

  return {
    returnTo,
    returnLabel: stateLabel || flow.returnLabel || getMessage("legal.backHome"),
  };
}
